import Container from 'components/container/container';
import ProductPage from 'components/product';
import {
  ProductDivider,
  ProductFeatureContainer,
  ProductFeatureLayoutGrid,
  ProductFeatureLayoutGridNode,
  ProductFeatureRequest,
  ProductFeatureShowMoreContainer,
  ProductFeatureShowMoreLink,
  ProductPlan,
  ProductQuote,
} from 'components/product/product.styles';
import Link from 'next/link';
import { useState } from 'react';
import Plan from 'shared/components/plan';

import { DYLAN_LINKEDIN, LIBUM_FEEDBACK, LIBUM_URL } from 'shared/constants/globals';
import { PRODUCTS } from 'shared/data/products';

const GLACI = PRODUCTS.find((p) => p.id === 'glaci')!;

export default function Glaci() {
  const title = 'Glaci | Libum';
  const description =
    'Glaci is a limitless creative platform for Symitar credit unions available as an overlay for Symitar Quest.';
  const keywords =
    'Glaci,Symitar overlay,Symitar Quest integration,Symitar Quest software,Symitar Quest alternative,Symitar Print Control,Symitar Batch Control,Jack Henry integration,Jack Henry software,credit union software';
  const url = `${LIBUM_URL}/glaci`;

  const [showMore, setShowMore] = useState(false);
  const handleShowMoreClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setShowMore(!showMore);
  };

  return (
    <Container title={title} description={description} keywords={keywords} url={url} socialCard="glaci">
      <div style={{ width: '100%' }}>
        <ProductPage product={GLACI}>
          <h1>Glaci</h1>
          <h2>A limitless creative platform for Symitar credit unions.</h2>
          <ProductDivider />
          <p id="overview">
            We are building an enhanced experience for Symitar Quest which brings blazing fast and beautiful UIs as well
            as incredible functionality to your credit union.
          </p>
          <p>
            We&apos;ve created things like <Link href="#playbooks">Playbooks</Link> and{' '}
            <Link href="#opportunities">Opportunities</Link> to reduce human error and help guide your employees to
            &quot;do the right thing&quot; plus cross-sell in realtime without any member research.
          </p>
          <p>
            On top of that, we&apos;ve also <Link href="#revamped-work-areas">revamped key work areas</Link> within
            Quest with elegant user interfaces that level up the UX for front and back-office staff with
            &quot;over-the-air&quot; updates to keep your organization future-proof.
          </p>
          <ProductDivider />
          <ProductFeatureContainer id="features">
            <ProductFeatureLayoutGrid className="featured glaci">
              {GLACI.features
                .filter((f) => f.isFeatured)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={`${feature.docsLink ? 'has-link' : ''}`}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid>
              {GLACI.features
                .filter((f) => !f.isFeatured && !f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid className={showMore ? 'opened' : 'hidden'}>
              {GLACI.features
                .filter((f) => f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
          </ProductFeatureContainer>
          <ProductFeatureShowMoreContainer>
            <Link href="" passHref legacyBehavior>
              <ProductFeatureShowMoreLink onClick={(e: any) => handleShowMoreClick(e)}>
                {showMore ? 'Show less' : 'Show more'}
              </ProductFeatureShowMoreLink>
            </Link>
          </ProductFeatureShowMoreContainer>
          <ProductDivider />
          <div>
            <h3 className="anchor" id="inspiration">
              <Link href="#inspiration">Why is this a big deal?</Link>
            </h3>
            <p>
              We&apos;ve built direct integration for native Symitar communication and then abstracted that to an
              overlay and bound it to the Quest graphical user interface (GUI). This means that now we can augment any
              user experience in Quest ranging from workflows with or without the PowerOn lifecycle to revamping entire
              work areas like Print Control.
            </p>
          </div>
          <ProductDivider />
          <ProductQuote>
            &quot;Glaci is simply a new way of developing for Symitar. We understand what it&apos;s like to have a
            laundry list of creative solutions waiting to be implemented - and struggling with how to make that
            available strictly through PowerOn.
            <br />
            <br />
            But what if you had another dimension by which to interact with Quest? <br />
            <br />
            With Glaci, we&apos;re introducing total creative freedom for Jack Henry credit unions without the typical
            constraints and development burden of building a platform yourself.&quot;
            <br />
            <br />{' '}
            <i>
              <Link href={DYLAN_LINKEDIN} target="_blank">
                Dylan Martinez, Co-Founder @ Libum
              </Link>
            </i>
          </ProductQuote>
          <ProductDivider />
          <ProductPlan id="offerings">
            <Plan header={GLACI.plan.header} features={GLACI.plan.features} offerings={GLACI.plan.offerings} />
          </ProductPlan>
          <ProductDivider />
          <ProductFeatureRequest>
            <h3>Have a feature you&apos;d like to see in Glaci?</h3>
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Submit Your Idea
            </Link>
          </ProductFeatureRequest>
        </ProductPage>
      </div>
    </Container>
  );
}
